import { useEffect, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import DataTable from "components/data-table";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { SearchInput } from "components/fields";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import { MdEdit, MdPersonAdd, MdPersonRemove } from "react-icons/md";
import { formatDate } from "utils/format";
import SettingsSection, { SettingsSubsection } from "./components/SettingsSection";
import ManageStaffDrawer from "./manage-staff";
import InviteStaffDrawer from "./invite-staff-drawer";
import PendingInvitations from "./pending-invitations";

const PROTECTED_ROLES = ["super_admin", "sub_admin"];

const StaffSettings = () => {
  const [staffList, setStaffList] = useState([]);
  const [staffDetails, setStaffDetails] = useState({});
  const [staffListLoading, setStaffListLoading] = useState(true);
  const [staffListError, setStaffListError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [invitationsRefresh, setInvitationsRefresh] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const confirm = useConfirm();

  const getStaffList = async () => {
    setStaffListLoading(true);
    setStaffListError(null);
    try {
      const response = await AtriumApi.get("/v1/get-staff");
      if (response) {
        const staffData = response?.data?.data?.rows || response?.data?.data || [];
        setStaffList(staffData);
      }
    } catch (error) {
      setStaffListError(error);
    } finally {
      setStaffListLoading(false);
    }
  };

  useEffect(() => {
    getStaffList();
  }, []);

  const handleRemoveStaffAccess = async (staff) => {
    if (PROTECTED_ROLES.includes(staff.role)) {
      notify.error("Super admin users cannot be demoted from this interface.");
      return;
    }

    const name = `${staff.firstName} ${staff.lastName}`;
    const confirmed = await confirm({
      title: `Remove staff access for "${name}"?`,
      message: "They will become a regular member and lose login access.",
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await AtriumApi.put(`/v1/users/${staff.id}/demote-staff`);
      notify.success("Staff access removed");
      getStaffList();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not remove staff access"));
    }
  };

  const query = searchQuery.toLowerCase();
  const filteredStaff = staffList.filter((staff) => {
    const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
    const email = (staff.email || "").toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const columns = [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => {
        const staff = row.original;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar user={staff} size="md" />
            <div className="min-w-0">
              <p className="truncate font-semibold text-fg">
                {staff?.firstName} {staff?.lastName}
              </p>
              <p className="truncate text-sm text-fg-muted">{staff?.email}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: "role",
      header: "Role",
      cell: ({ row }) => (
        <Badge tone="neutral">{row.original.role === "admin" ? "Admin" : "Staff"}</Badge>
      ),
    },
    {
      accessorKey: "lastLoginAt",
      header: "Last Login",
      cell: ({ getValue }) => (getValue() ? formatDate(getValue()) : "Never"),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { align: "right" },
      cell: ({ row }) => {
        const staff = row.original;
        const name = `${staff.firstName} ${staff.lastName}`;
        return (
          <div className="flex items-center justify-end gap-1">
            <IconButton
              icon={<MdEdit />}
              label={`Edit ${name}`}
              size="sm"
              onClick={() => {
                setStaffDetails(staff);
                setIsDrawerOpen(true);
              }}
            />
            <IconButton
              icon={<MdPersonRemove />}
              label={`Remove staff access for ${name}`}
              size="sm"
              onClick={() => handleRemoveStaffAccess(staff)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SettingsSection
        title="User Management"
        description="Manage staff members and invitations."
        action={
          <Button leftIcon={<MdPersonAdd />} onClick={() => setIsInviteDrawerOpen(true)}>
            Invite Users
          </Button>
        }
      >
        <SettingsSubsection title="Pending Invitations">
          <PendingInvitations refresh={invitationsRefresh} />
        </SettingsSubsection>

        <SettingsSubsection
          title="Current Staff"
          action={
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by name or email…"
              ariaLabel="Search staff"
              className="w-full sm:w-72"
            />
          }
        >
          {staffListError ? (
            <ErrorState
              message={getApiErrorMessage(staffListError, "Could not load staff.")}
              onRetry={getStaffList}
            />
          ) : (
            <DataTable
              variant="plain"
              aria-label="Current staff"
              columns={columns}
              data={filteredStaff}
              loading={staffListLoading}
              getRowId={(s) => String(s.id)}
              emptyState={
                searchQuery ? (
                  <EmptyState
                    variant="filtered"
                    title="No staff match your search"
                    action={
                      <Button variant="ghost" onClick={() => setSearchQuery("")}>
                        Clear Search
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState variant="inline" title="No staff yet. Invite users to get started." />
                )
              }
            />
          )}
        </SettingsSubsection>
      </SettingsSection>

      {isInviteDrawerOpen && (
        <InviteStaffDrawer
          isOpen={isInviteDrawerOpen}
          onClose={() => setIsInviteDrawerOpen(false)}
          onInviteSent={() => {
            setInvitationsRefresh((prev) => prev + 1);
            getStaffList();
          }}
        />
      )}

      {isDrawerOpen && (
        <ManageStaffDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          user={staffDetails}
          getStaffList={getStaffList}
        />
      )}
    </>
  );
};

export default StaffSettings;
